import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  CheckCircle,
  Ban,
  CreditCard,
  Calendar,
  Phone,
  Mail,
  FileText,
  Settings,
} from 'lucide-react';
import { toast } from 'sonner';
import { superFetch, formatCurrency, formatDate } from '@/lib/super-admin-api';
import type { Tenant, Plan } from '@/lib/super-admin-types';

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [showPlanChange, setShowPlanChange] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [tenantRes, plansRes] = await Promise.all([
          superFetch(`/api/super-admin/tenants/${id}`),
          superFetch('/api/super-admin/plans'),
        ]);

        if (tenantRes.status === 404) {
          setNotFound(true);
          return;
        }

        if (tenantRes.ok) {
          const data = await tenantRes.json();
          setTenant(data);
          setSelectedPlan(data.plan_id);
        }

        if (plansRes.ok) {
          const data = await plansRes.json();
          setPlans(Array.isArray(data) ? data : []);
        }
      } catch {
        toast.error('Erro ao carregar dados do estabelecimento');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  async function handleStatusChange(newStatus: 'ativo' | 'bloqueado' | 'inativo') {
    if (!tenant) return;
    setChangingStatus(true);
    try {
      const res = await superFetch(`/api/super-admin/tenants/${tenant.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenant({ ...tenant, ...updated });
        toast.success(`Status alterado para: ${newStatus}`);
      } else {
        toast.error('Erro ao alterar status');
      }
    } catch {
      toast.error('Erro de conexão');
    } finally {
      setChangingStatus(false);
    }
  }

  async function handlePlanChange() {
    if (!tenant || !selectedPlan || selectedPlan === tenant.plan_id) return;
    setChangingPlan(true);
    try {
      const res = await superFetch(`/api/super-admin/tenants/${tenant.id}/plan`, {
        method: 'PATCH',
        body: JSON.stringify({ planId: selectedPlan }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTenant({ ...tenant, ...updated });
        setShowPlanChange(false);
        toast.success('Plano alterado com sucesso!');
      } else {
        const err = await res.json();
        toast.error(err.error || 'Erro ao alterar plano');
      }
    } catch {
      toast.error('Erro de conexão');
    } finally {
      setChangingPlan(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-64 rounded" style={{ background: '#334155' }} />
          <div className="h-64 rounded-xl" style={{ background: '#1e293b' }} />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh]">
        <Building2 className="h-16 w-16 mb-4" style={{ color: '#475569' }} />
        <h2 className="text-xl font-bold mb-2" style={{ color: '#f1f5f9' }}>Estabelecimento não encontrado</h2>
        <p className="text-base mb-6" style={{ color: '#94a3b8' }}>O ID informado não corresponde a nenhum tenant.</p>
        <button
          onClick={() => navigate('/super-admin/tenants')}
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium"
          style={{ background: '#334155', color: '#e2e8f0' }}
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para lista
        </button>
      </div>
    );
  }

  if (!tenant) return null;

  const statusColors: Record<string, { bg: string; color: string }> = {
    ativo: { bg: '#10b98120', color: '#10b981' },
    bloqueado: { bg: '#ef444420', color: '#ef4444' },
    inativo: { bg: '#64748b20', color: '#94a3b8' },
  };

  const sc = statusColors[tenant.status] || statusColors.inativo!;

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb + Back */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/super-admin/tenants')}
          className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors hover:opacity-80"
          style={{ background: '#334155', color: '#e2e8f0' }}
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar
        </button>
        <div className="flex items-center gap-2 text-sm" style={{ color: '#64748b' }}>
          <span>Estabelecimentos</span>
          <span>/</span>
          <span style={{ color: '#f1f5f9' }}>{tenant.nome_fantasia}</span>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#f1f5f9' }}>{tenant.nome_fantasia}</h1>
          <p className="text-base mt-1" style={{ color: '#94a3b8' }}>{tenant.razao_social}</p>
        </div>
        <span
          className="rounded-full px-3 py-1.5 text-sm font-medium"
          style={{ background: sc.bg, color: sc.color }}
        >
          {tenant.status === 'ativo' ? 'Ativo' : tenant.status === 'bloqueado' ? 'Bloqueado' : 'Inativo'}
        </span>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Info */}
        <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#f1f5f9' }}>
            <FileText className="h-5 w-5" style={{ color: '#3b82f6' }} />
            Informações Gerais
          </h3>
          <div className="space-y-4">
            <InfoRow label="CNPJ" value={tenant.cnpj} />
            <InfoRow label="Razão Social" value={tenant.razao_social} />
            <InfoRow label="Telefone" value={tenant.telefone || '—'} icon={<Phone className="h-4 w-4" />} />
            <InfoRow label="Schema" value={tenant.schema_name} />
            <InfoRow label="Criado em" value={formatDate(tenant.created_at)} icon={<Calendar className="h-4 w-4" />} />
          </div>
        </div>

        {/* Plan & Subscription */}
        <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#f1f5f9' }}>
            <CreditCard className="h-5 w-5" style={{ color: '#10b981' }} />
            Plano & Assinatura
          </h3>
          <div className="space-y-4">
            <InfoRow label="Plano Atual" value={tenant.plan_name || '—'} />
            <InfoRow label="Preço" value={formatCurrency(Number(tenant.plan_price || 0))} />
            <InfoRow
              label="Vencimento"
              value={tenant.subscription_expires_at ? formatDate(tenant.subscription_expires_at) : '—'}
              icon={<Calendar className="h-4 w-4" />}
            />
            <InfoRow
              label="Onboarding"
              value={tenant.onboarding_complete ? 'Completo ✓' : 'Pendente'}
              valueColor={tenant.onboarding_complete ? '#10b981' : '#f59e0b'}
            />
          </div>

          {/* Change Plan */}
          <div className="mt-6 pt-4" style={{ borderTop: '1px solid #334155' }}>
            {!showPlanChange ? (
              <button
                onClick={() => setShowPlanChange(true)}
                className="text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: '#3b82f6' }}
              >
                Alterar plano →
              </button>
            ) : (
              <div className="space-y-3">
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="w-full rounded-lg px-3 py-2.5 text-base outline-none"
                  style={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }}
                >
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {formatCurrency(p.price)}/mês
                    </option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={handlePlanChange}
                    disabled={changingPlan || selectedPlan === tenant.plan_id}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                    style={{ background: '#10b981' }}
                  >
                    {changingPlan ? 'Salvando...' : 'Confirmar'}
                  </button>
                  <button
                    onClick={() => { setShowPlanChange(false); setSelectedPlan(tenant.plan_id); }}
                    className="rounded-lg px-3 py-2 text-sm font-medium"
                    style={{ color: '#94a3b8' }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status Actions */}
      <div className="rounded-xl border p-6" style={{ background: '#1e293b', borderColor: '#334155' }}>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: '#f1f5f9' }}>
          <Settings className="h-5 w-5" style={{ color: '#f59e0b' }} />
          Ações
        </h3>
        <div className="flex flex-wrap gap-3">
          {tenant.status !== 'ativo' && (
            <button
              onClick={() => handleStatusChange('ativo')}
              disabled={changingStatus}
              className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ background: '#10b981' }}
            >
              <CheckCircle className="h-4 w-4" />
              Ativar
            </button>
          )}
          {tenant.status !== 'bloqueado' && (
            <button
              onClick={() => handleStatusChange('bloqueado')}
              disabled={changingStatus}
              className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ background: '#ef4444' }}
            >
              <Ban className="h-4 w-4" />
              Bloquear
            </button>
          )}
          {tenant.status !== 'inativo' && (
            <button
              onClick={() => handleStatusChange('inativo')}
              disabled={changingStatus}
              className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base font-medium transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ background: '#334155', color: '#94a3b8' }}
            >
              Desativar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  icon,
  valueColor,
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 text-sm" style={{ color: '#94a3b8' }}>
        {icon}
        {label}
      </span>
      <span className="text-base font-medium" style={{ color: valueColor || '#f1f5f9' }}>
        {value}
      </span>
    </div>
  );
}
